<template>
  <article
    class="ofi-row"
    :class="[`ofi-row--${screenMode}`, index % 2 ? 'ofi-row--alt' : '']"
    data-screen-label="listing-row"
  >
    <!-- time -->
    <div class="ofi-row__time">
      <template v-if="screenMode === 'narrow'">
        <span class="ofi-row__time-start">{{ time.start }}</span>
        <span v-if="time.end" class="ofi-row__time-end">– {{ time.end }}</span>
      </template>
      <template v-else>
        <div class="ofi-row__time-start">{{ time.start }}</div>
        <div v-if="time.end" class="ofi-row__time-end">– {{ time.end }}</div>
      </template>
    </div>

    <!-- address -->
    <div class="ofi-row__address">
      <div class="ofi-row__street">{{ street }}</div>
      <div v-if="suburb" class="ofi-row__suburb">{{ suburb }}</div>
    </div>

    <!-- features -->
    <div class="ofi-row__features">
      <div class="ofi-row__feature">
        <svg
          class="ofi-row__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M3 11V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4"></path>
          <path d="M2 17v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3"></path>
          <path d="M2 17v2"></path>
          <path d="M22 17v2"></path>
        </svg>
        <span class="ofi-row__feature-value">{{ beds }}</span>
      </div>
      <div class="ofi-row__feature">
        <svg
          class="ofi-row__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M4 12h16a1 1 0 0 1 1 1 6 6 0 0 1-6 6H9a6 6 0 0 1-6-6 1 1 0 0 1 1-1Z"
          ></path>
          <path d="M6 12V5a2 2 0 0 1 4 0"></path>
          <path d="M12 19v2"></path>
          <path d="M8 19v2"></path>
          <path d="M16 19v2"></path>
        </svg>
        <span class="ofi-row__feature-value">{{ baths }}</span>
      </div>
      <div class="ofi-row__feature">
        <svg
          class="ofi-row__icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M5 11 6.5 6.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11"></path>
          <path d="M3 16v-3a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3"></path>
          <circle cx="7" cy="16" r="1.4"></circle>
          <circle cx="17" cy="16" r="1.4"></circle>
          <path d="M3 16v2"></path>
          <path d="M21 16v2"></path>
        </svg>
        <span class="ofi-row__feature-value">{{ cars }}</span>
      </div>
    </div>

    <!-- price -->
    <div class="ofi-row__price">
      <template v-if="price">
        <div class="ofi-row__price-label">{{ price.label }}</div>
        <div class="ofi-row__price-main">{{ price.main }}</div>
      </template>
    </div>
  </article>
</template>

<script>
import Vue from "vue";
import dayjs from "dayjs";

export default Vue.extend({
  name: "PropertyItem",
  props: {
    index: Number,
    property: {
      type: Object,
      default: () => ({}),
    },
    screenMode: {
      type: String,
      default: "wide",
    },
  },
  computed: {
    time() {
      const slot = (this.property.inspectionTimes || [])[0];
      if (!slot) return { start: "N/A", end: "" };
      const start = dayjs(slot.startDateTime);
      const end = dayjs(slot.endDateTime);
      return {
        start: start.isValid() ? start.format("h:mm a") : "N/A",
        end: end.isValid() ? end.format("h:mm a") : "",
      };
    },
    street() {
      const a = this.property.address || {};
      return [a.streetNumber, a.street].filter(Boolean).join(" ").trim() || "—";
    },
    suburb() {
      return (this.property.address || {}).suburb || "";
    },
    beds() {
      return (this.property.features || {}).bedrooms || 0;
    },
    baths() {
      return (this.property.features || {}).bathrooms || 0;
    },
    cars() {
      const f = this.property.features || {};
      return f.parkingSpaces || f.openSpaces || f.garages || 0;
    },
    // The feed exposes price as one free-text string. Split it into the
    // design's label + value pair, and drop the block entirely when empty.
    price() {
      const raw = String(this.property.price ?? "").trim();
      if (!raw || raw === "0") return null;

      const lower = raw.toLowerCase();
      let label = "Price";
      if (lower.includes("auction")) label = "Auction Guide";
      else if (/[-–—]\s*\$/.test(raw) || / to \$/i.test(raw))
        label = "Price Range";
      else if (lower.includes("offer")) label = "Offers";
      else if (lower.includes("contact") || lower.includes("poa"))
        label = "Enquire";

      const main =
        raw
          .replace(/^(auction guide|auction|price range|price|guide)\s*[-:]?\s*/i, "")
          .trim() || raw;

      return { label, main };
    },
  },
});
</script>

<style>
.ofi-row {
  flex: 1 1 0;
  min-height: 0;
  max-height: var(--row-max);
  display: grid;
  align-content: center;
  gap: var(--gap-row-inner) var(--gap-col);
  background: var(--c2-06);
  border: 1px solid var(--c2-08);
  border-radius: var(--radius);
  padding: var(--row-pad-y) var(--row-pad-x);
  overflow: hidden;
}

.ofi-row--alt {
  background: var(--c2-03);
}

/* time */
.ofi-row__time {
  grid-area: time;
  min-width: 0;
}

.ofi-row__time-start {
  font-size: var(--fs-time);
  font-weight: 700;
  color: var(--c3);
  line-height: 1.1;
  white-space: nowrap;
}

.ofi-row__time-end {
  font-size: var(--fs-sub);
  font-weight: 500;
  color: var(--c2-55);
  line-height: 1.1;
  white-space: nowrap;
}

/* address */
.ofi-row__address {
  grid-area: addr;
  min-width: 0;
}

.ofi-row__street {
  font-size: var(--fs-street);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.01em;
  text-wrap: balance;
  overflow-wrap: anywhere;
}

.ofi-row__suburb {
  font-size: var(--fs-sub);
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--c2-60);
  margin-top: 0.25em;
  overflow-wrap: anywhere;
}

/* features */
.ofi-row__features {
  grid-area: feat;
  display: flex;
  align-items: center;
  gap: clamp(12px, 1.2vw, 32px);
}

.ofi-row__feature {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ofi-row__icon {
  width: var(--fs-icon);
  height: var(--fs-icon);
  flex: none;
  color: var(--c2-60);
}

.ofi-row__feature-value {
  font-size: var(--fs-feature);
  font-weight: 800;
  line-height: 1;
}

/* price */
.ofi-row__price {
  grid-area: price;
  text-align: right;
  min-width: 0;
}

.ofi-row__price-label {
  font-size: var(--fs-price-label);
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--c2-55);
}

.ofi-row__price-main {
  font-size: var(--fs-price);
  font-weight: 800;
  color: var(--c3);
  white-space: nowrap;
  margin-top: 0.15em;
}

/* ------------------------------------------------------------ wide
   1920x1080, 1920x960, 1385x960, 1385x1080 — everything on one line. */
.ofi-row--wide {
  grid-template-areas: "time addr feat price";
  grid-template-columns: auto minmax(0, 1fr) auto auto;
  align-items: center;
}

/* --------------------------------------------------------- compact
   1080x1920, 1080x1800, 1080x1680 — two lines. */
.ofi-row--compact {
  grid-template-areas:
    "time addr"
    "feat price";
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
}

.ofi-row--compact .ofi-row__price {
  justify-self: end;
}

/* ---------------------------------------------------------- narrow
   535x960, 535x1080, 640x1080 — fully stacked. */
.ofi-row--narrow {
  grid-template-areas:
    "time  time"
    "addr  addr"
    "feat  price";
  grid-template-columns: auto minmax(0, 1fr);
  align-items: end;
}

.ofi-row--narrow .ofi-row__time {
  display: flex;
  align-items: baseline;
  gap: 0.4em;
}

.ofi-row--narrow .ofi-row__price {
  justify-self: end;
}

.ofi-row--narrow .ofi-row__price-main {
  white-space: normal;
  overflow-wrap: anywhere;
}

.ofi-row--narrow .ofi-row__suburb {
  letter-spacing: 0.16em;
}

.ofi-row--narrow .ofi-row__features {
  gap: clamp(10px, 3vw, 24px);
}
</style>
